import { ArrowBack, DeleteForever, Edit, Lock, LockOpen, PictureAsPdf } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import { Button, Card, CardContent, Divider, Grid } from '@mui/material'
import PDFObjectBase64 from 'components/PDFObjectBase64'
import { useAuth, useLayout } from 'context'
import * as controllers from 'controllers'
import { ClusterShipperThirdPartyConversionDeliveryDto, MichiganSOSBundleDto } from 'dtos'
import { useLoadingState } from 'hooks'
import { enqueueSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import MichiganSOSBundlesManageVehiclesListCard from './ManageVehiclesCard'
import MichiganSOSBundleAddEditDialog from './MichiganSOSBundlesAddEditDialog'
import MichiganSOSBundlesDetailsCard from './MichiganSOSBundlesDetailsCard'
import MichiganSOSBundlesTitleApplicationCoverLetterListCard from './MichiganSOSBundlesTitleApplicationCoverLetterListCard'
import MichiganSOSBundlesTitleApplicationDeliveryPhotoCard from './TitleApplicationDeliveryPhotoCard'

export interface ClusterShippersThirdPartyConversionDeliveryPhotoCardProps {
  thirdParyClusterConversionDeliveryPhoto: ClusterShipperThirdPartyConversionDeliveryDto
  setThirdPartyClusterConversionDeliveryPhoto: React.Dispatch<React.SetStateAction<ClusterShipperThirdPartyConversionDeliveryDto>>
}

export default function MichiganSOSBundlesDetails() {
  const { michiganSOSBundleGuid } = useParams()
  const layout = useLayout()
  const navigate = useNavigate()
  const { permissions } = useAuth()

  const [loadingState, setLoadingState] = useLoadingState({
    isGettingMichiganSOSBundle: false,
    isUpdatingMichiganSOSBundle: false,
    isUnlockingMichiganSOSBundle: false,
    isLockingMichiganSOSBundle: false,
    isDeletingMichiganSOSBundle: false,
    isGeneratingMichiganSOSBundle: false
  })

  const [michiganSOSBundle, setMichiganSOSBundle] = useState<MichiganSOSBundleDto>(new MichiganSOSBundleDto())
  const [michiganSOSBundleAddEditDialogOpen, setMichiganSOSBundleAddEditDialogOpen] = useState<boolean>(false)
  const [base64, setBase64] = useState<string>('')
  const [isGeneratingMichiganSOSBundle, setIsGeneratingMichiganSOSBundle] = useState<boolean>(false)

  useEffect(() => {
    if (michiganSOSBundleGuid && michiganSOSBundleGuid.toLowerCase() !== 'undefined' && michiganSOSBundleGuid.toLowerCase() !== 'new') {
      setLoadingState('isGettingMichiganSOSBundle', true)
      controllers.michiganSoSBundles
        .getMichiganSOSBundle(michiganSOSBundleGuid)
        .then(response => {
          setMichiganSOSBundle(response.value)
        })
        .finally(() => {
          setLoadingState('isGettingMichiganSOSBundle', false)
        })
    }
  }, [michiganSOSBundleGuid])

  useEffect(() => {
    layout.setTitle('Michigan SOS Bundle Details')

    return () => {
      layout.setTitle('')
    }
  }, [layout])

  useEffect(() => {
    layout.setIsLayoutLoading(
      loadingState.isGettingMichiganSOSBundle ||
        loadingState.isLockingMichiganSOSBundle ||
        loadingState.isUnlockingMichiganSOSBundle ||
        loadingState.isDeletingMichiganSOSBundle
    )
  }, [
    loadingState.isGettingMichiganSOSBundle,
    loadingState.isLockingMichiganSOSBundle,
    loadingState.isUnlockingMichiganSOSBundle,
    loadingState.isDeletingMichiganSOSBundle
  ])

  return (
    <>
      <MichiganSOSBundleAddEditDialog
        initialValues={michiganSOSBundle}
        isLoading={loadingState.isUpdatingMichiganSOSBundle}
        onClose={() => {
          setMichiganSOSBundleAddEditDialogOpen(false)
        }}
        onSave={values => {
          setLoadingState('isUpdatingMichiganSOSBundle', true)
          controllers.michiganSoSBundles
            .updateMichiganSOSBundle({
              // There is a bug here where the VehicleDto's validation is firing.
              // This is a workaround until we split up the VehicleDto for this context/scenario.
              michiganSOSBundleGuid: values.michiganSOSBundleGuid,
              michiganTitleOwnerName: values.michiganTitleOwnerName
            })
            .then(response => {
              setMichiganSOSBundle(response.value)
              setMichiganSOSBundleAddEditDialogOpen(false)
              enqueueSnackbar('Michigan SOS Bundle Saved Successfully!', { variant: 'success' })
            })
            .finally(() => {
              setLoadingState('isUpdatingMichiganSOSBundle', false)
            })
        }}
        open={michiganSOSBundleAddEditDialogOpen}
      />

      <Grid container spacing={1} alignItems='flex-start'>
        <Grid item xs={12}>
          <MichiganSOSBundlesDetailsCard michiganSOSBundle={michiganSOSBundle}>
            <Grid container direction='row' spacing={1} alignItems='center'>
              <Grid item>
                <Button
                  disabled={loadingState.isUpdatingMichiganSOSBundle || loadingState.isGettingMichiganSOSBundle}
                  onClick={() => navigate('/titles/michigan-sos-bundles')}
                  color='primary'
                  startIcon={<ArrowBack />}
                  variant='text'
                >
                  BACK
                </Button>
              </Grid>

              <Grid item sx={{ alignSelf: 'stretch' }}>
                <Divider orientation='vertical' />
              </Grid>

              <Grid item>
                <LoadingButton
                  disabled={!michiganSOSBundle.isLocked}
                  endIcon={<PictureAsPdf />}
                  loading={isGeneratingMichiganSOSBundle}
                  loadingPosition='end'
                  onClick={() => {
                    setIsGeneratingMichiganSOSBundle(true)
                    controllers.michiganSoSBundles
                      .generateTitleApplicationCoverLetter(michiganSOSBundle.michiganSOSBundleGuid!)
                      .finally(() => setIsGeneratingMichiganSOSBundle(false))
                  }}
                  variant='text'
                >
                  GENERATE MI TITLE APPLICATION COVER LETTER
                </LoadingButton>
              </Grid>

              <Grid item>
                <Button
                  endIcon={michiganSOSBundle.isLocked ? <LockOpen /> : <Lock />}
                  onClick={() => {
                    if (michiganSOSBundle.isLocked === true) {
                      setLoadingState('isUnlockingMichiganSOSBundle', true)

                      controllers.michiganSoSBundles
                        .unlockMichiganSOSBundle(michiganSOSBundle.michiganSOSBundleGuid!)
                        .then(response => {
                          setMichiganSOSBundle(response.value)
                          enqueueSnackbar('Michigan SOS Bundle Unlocked Successfully!', { variant: 'success' })
                        })
                        .finally(() => setLoadingState('isUnlockingMichiganSOSBundle', false))
                    } else {
                      setLoadingState('isLockingMichiganSOSBundle', true)

                      controllers.michiganSoSBundles
                        .lockMichiganSOSBundle(michiganSOSBundle.michiganSOSBundleGuid!)
                        .then(response => {
                          setMichiganSOSBundle(response.value)
                          enqueueSnackbar('Michigan SOS Bundle Locked Successfully!', { variant: 'success' })
                        })
                        .finally(() => setLoadingState('isLockingMichiganSOSBundle', false))
                    }
                  }}
                  variant='text'
                >
                  {michiganSOSBundle.isLocked ? 'UNLOCK' : 'LOCK'}
                </Button>
              </Grid>

              <Grid item>
                <Button
                  color='error'
                  disabled={michiganSOSBundle.isLocked}
                  endIcon={<DeleteForever />}
                  onClick={() => {
                    if (window.confirm('Are you sure you wish to delete this Michigan SoS Bundle? This cannot be undone.')) {
                      setLoadingState('isDeletingMichiganSOSBundle', true)
                      controllers.michiganSoSBundles
                        .deleteMichiganSOSBundle(michiganSOSBundle.michiganSOSBundleGuid!)
                        .then(() => {
                          enqueueSnackbar('Michigan SOS Bundle Deleted Successfully!', { variant: 'success' })
                          navigate('/titles/michigan-sos-bundles', { replace: true })
                        })
                        .finally(() => {
                          setLoadingState('isDeletingMichiganSOSBundle', false)
                        })
                    }
                  }}
                  variant='text'
                >
                  DELETE
                </Button>
              </Grid>

              <Grid item>
                <Button
                  disabled={michiganSOSBundle.isLocked}
                  endIcon={<Edit />}
                  onClick={() => {
                    setMichiganSOSBundleAddEditDialogOpen(true)
                  }}
                  variant='text'
                >
                  EDIT
                </Button>
              </Grid>
            </Grid>
          </MichiganSOSBundlesDetailsCard>
        </Grid>

        <Grid item xs={12} sm={7} container spacing={1} alignItems='flex-start'>
          <Grid item xs={12}>
            <MichiganSOSBundlesManageVehiclesListCard michiganSOSBundle={michiganSOSBundle} setMichiganSOSBundle={setMichiganSOSBundle} />
          </Grid>

          <Grid item xs={12}>
            <MichiganSOSBundlesTitleApplicationDeliveryPhotoCard michiganSOSBundle={michiganSOSBundle} />
          </Grid>
        </Grid>

        <Grid item xs={12} sm={5}>
          <MichiganSOSBundlesTitleApplicationCoverLetterListCard michiganSOSBundle={michiganSOSBundle} />
        </Grid>
      </Grid>

      {base64 && (
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <PDFObjectBase64 base64={base64} />
            </CardContent>
          </Card>
        </Grid>
      )}
    </>
  )
}
